import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  GetMemoriesQueryParamsSchema,
  MemoryConfigResponseSchema,
  MemoryConfigSchema,
  MemoryRuleDbSchema,
  MemoryRuleSchema,
  MemorySchema,
  mcpExposeMetadata,
  OKGeneric,
  PostCreateMemoriesPayloadSchema,
  ResponseMetaSchema,
  TrackerMemoryRawSchema,
  URLParamsSchema,
} from '../models';
import { apiMemoriesTrackers } from './contract.memories.trackers';

extendZodWithOpenApi(z);
const c = initContract();

/**
 * Paths
 */
export const apiMemories = c.router({
  getMemoryConfig: {
    method: 'GET',
    path: `/1.0/memories/filters`,
    description: `Returns the authenticated user's filters for Memories.`,
    responses: {
      200: MemoryConfigResponseSchema,
    },
  },
  saveMemoryConfig: {
    method: 'POST',
    path: `/1.0/memories/filters`,
    description: "Saves the authenticated user's filters for Memories. ",
    body: MemoryConfigSchema,
    responses: {
      200: MemoryConfigResponseSchema,
    },
  },
  getMemoryRules: {
    method: 'GET',
    path: `/1.0/memories/rules`,
    description: `Returns the authenticated user's rules for Memories.`,
    responses: {
      // Not sure I like the separate keys for `rules` and `rule` here, discuss ideals with team.
      // then making it consistent across the API -- many instances of inconsistencies.
      // This isn't a really a big deal for `fetch` and `react-query` clients, but consider Redux/RTK.
      200: z.object({
        rules: z.array(MemoryRuleDbSchema),
        meta: ResponseMetaSchema.pick({ count: true, totalCount: true }),
      }),
    },
  },
  saveMemoryRule: {
    method: 'POST',
    path: `/1.0/memories/rules`, // Dynamic params would be nice here, but not sure how -- {id?} didn't work
    description: `Creates or updates a rule for the authenticated user.`,
    body: MemoryRuleDbSchema.or(MemoryRuleSchema),
    responses: {
      200: z.object({ rule: MemoryRuleDbSchema }),
    },
  },
  getMemories: {
    method: 'GET',
    path: `/1.0/memories`,
    summary: 'Browse memories in a date range',
    description:
      'Returns Memories in chronological order within a required start/end range. LOCAL-DEV/DEBUG ONLY: intended for admin/debug deep browsing, Provider CLI, and MCP tooling (not a production firehose).',
    query: GetMemoriesQueryParamsSchema,
    responses: {
      200: z
        .object({
          memories: z.array(MemorySchema),
          meta: ResponseMetaSchema.pick({ count: true, totalCount: true, message: true }),
          // query: MemoryConstructionParamsSchema,
        })
        .passthrough(),
    },
    metadata: mcpExposeMetadata,
  },
  // getMemoriesForAiFirehose moved to contract.ai.memories.ts
  createMemories: {
    method: 'POST',
    path: `/1.0/memories/bulk`,
    description: `Creates new Memories`,
    body: PostCreateMemoriesPayloadSchema,
    responses: {
      204: OKGeneric,
      200: z.object({
        max_memories_per_upload: z.number().int().min(100).max(1000).default(500),
        max_upload_frequency_in_seconds: z.number().int().min(0).default(0).openapi({
          description:
            'Memory App should not upload more frequenty than every X seconds. No rate limit if 0.',
          example: 3,
        }),
      }),
    },
  },
  createMemory: {
    method: 'POST',
    path: `/1.0/memories`,
    query: z.object({
      accountId: URLParamsSchema.accountId,
    }),
    description: `Creates a new Memory`,
    body: TrackerMemoryRawSchema,
    responses: {
      201: OKGeneric,
    },
  },
  deleteMemories: {
    method: 'DELETE',
    path: `/1.0/memories`,
    description: `Deletes Memories`,
    body: z.array(z.number().int()).max(10_000),
    responses: {
      200: OKGeneric,
    },
  },
  ignoreMemories: {
    method: 'POST',
    path: `/1.0/memories/ignore`,
    description: `Ignores Memories`,
    body: z.array(z.number().int()),
    responses: {
      200: OKGeneric,
    },
  },
  unignoreMemories: {
    method: 'POST',
    path: `/1.0/memories/unignore`,
    description: `Unignores Memories`,
    body: z.array(z.number().int()),
    responses: {
      200: OKGeneric,
    },
  },
  trackers: apiMemoriesTrackers,
});
